import { createStepRegistry } from "../../utils/flowAnalytics/createStepRegistry.js";
import { normalizeEntryFlowSlug, normalizeFlowSlug } from "../../utils/flowAnalytics/flowSlug.js";
import CommonCegjelzoAuto from "../steps/common/CommonCegjelzoAuto.jsx";
import CommonCompanyType from "../steps/common/CommonCompanyType.jsx";
import CommonFillingMethod from "../steps/common/CommonFillingMethod.jsx";
import CommonKftCegjelzoInterstitial from "../steps/common/CommonKftCegjelzoInterstitial.jsx";
import CommonKftInterstitial from "../steps/common/CommonKftInterstitial.jsx";
import CommonMoreBranch from "../steps/common/CommonMoreBranch.jsx";
import CommonRouting from "../steps/common/CommonRouting.jsx";
import CommonValueProp from "../steps/common/CommonValueProp.jsx";
import NewMultiCompanyInterstitial from "../steps/common/NewMultiCompanyInterstitial.jsx";
import CompanyCegjelzoTaxForm from "../steps/company/CompanyCegjelzoTaxForm.jsx";
import CompanyContactPage from "../steps/company/CompanyContactPage.jsx";
import CompanyEmployeeNo from "../steps/company/CompanyEmployeeNo.jsx";
import CompanyForeignTrade from "../steps/company/CompanyForeignTrade.jsx";
import CompanyIndustryActivity from "../steps/company/CompanyIndustryActivity.jsx";
import CompanyIndustrySelect from "../steps/company/CompanyIndustrySelect.jsx";
import CompanyQuickResult from "../steps/company/CompanyQuickResult.jsx";
import CompanyRevenue from "../steps/company/CompanyRevenue.jsx";
import CompanySuccessScreen from "../steps/company/CompanySuccessScreen.jsx";
import CompanyTaxForm from "../steps/company/CompanyTaxForm.jsx";
import CompanyVatStatus from "../steps/company/CompanyVatStatus.jsx";
import EvCegjelzoTaxForm from "../steps/ev/EvCegjelzoTaxForm.jsx";
import EvContactPage from "../steps/ev/EvContactPage.jsx";
import EvForeignTradeStep from "../steps/ev/EvForeignTrade.jsx";
import EvIndustryActivity from "../steps/ev/EvIndustryActivity.jsx";
import EvIndustrySelect from "../steps/ev/EvIndustrySelect.jsx";
import EvQuickResult from "../steps/ev/EvQuickResult.jsx";
import EvRevenue from "../steps/ev/EvRevenue.jsx";
import EvSuccessScreen from "../steps/ev/EvSuccessScreen.jsx";
import EvTaxForm from "../steps/ev/EvTaxForm.jsx";
import EvVatStatus from "../steps/ev/EvVatStatus.jsx";
import NewCompanyType from "../steps/other/NewCompanyType.jsx";

/**
 * Stable step metadata keyed by React component.
 * stepId values are persisted in MongoDB — do not rename; add new entries for new steps.
 */
const STEP_META_BY_COMPONENT = new Map([
  [CommonValueProp, { id: "common_value_prop", phase: "common" }],
  [CommonRouting, { id: "common_routing", phase: "common" }],
  [CommonMoreBranch, { id: "common_more_branch", phase: "common" }],
  [CommonFillingMethod, { id: "common_filling_method", phase: "common" }],
  [CommonCegjelzoAuto, { id: "common_cegjelzo_auto", phase: "common" }],
  [EvCegjelzoTaxForm, { id: "ev_cegjelzo_tax_form", phase: "ev" }],
  [CompanyCegjelzoTaxForm, { id: "company_cegjelzo_tax_form", phase: "company" }],
  [CommonCompanyType, { id: "common_company_type", phase: "common" }],
  [CommonKftInterstitial, { id: "common_kft_interstitial", phase: "common" }],
  [CommonKftCegjelzoInterstitial, { id: "common_kft_cegjelzo_interstitial", phase: "common" }],
  [NewMultiCompanyInterstitial, { id: "new_multi_company_interstitial", phase: "common" }],
  [CompanyIndustrySelect, { id: "company_industry_select", phase: "company" }],
  [CompanyIndustryActivity, { id: "company_industry_activity", phase: "company" }],
  [CompanyQuickResult, { id: "company_quick_result", phase: "company" }],
  [CompanyTaxForm, { id: "company_tax_form", phase: "company" }],
  [CompanyEmployeeNo, { id: "company_employee_no", phase: "company" }],
  [CompanyVatStatus, { id: "company_vat_status", phase: "company" }],
  [CompanyRevenue, { id: "company_revenue", phase: "company" }],
  [CompanyForeignTrade, { id: "company_foreign_trade", phase: "company" }],
  [CompanyContactPage, { id: "company_contact_page", phase: "company" }],
  [CompanySuccessScreen, { id: "company_success_screen", phase: "company" }],
  [EvIndustrySelect, { id: "ev_industry_select", phase: "ev" }],
  [EvIndustryActivity, { id: "ev_industry_activity", phase: "ev" }],
  [EvQuickResult, { id: "ev_quick_result", phase: "ev" }],
  [EvTaxForm, { id: "ev_tax_form", phase: "ev" }],
  [EvVatStatus, { id: "ev_vat_status", phase: "ev" }],
  [EvRevenue, { id: "ev_revenue", phase: "ev" }],
  [EvForeignTradeStep, { id: "ev_foreign_trade", phase: "ev" }],
  [EvContactPage, { id: "ev_contact_page", phase: "ev" }],
  [EvSuccessScreen, { id: "ev_success_screen", phase: "ev" }],
  [NewCompanyType, { id: "new_company_type", phase: "common" }],
]);

/**
 * Context fields owned by each step — only these are sent on events for that step.
 * Persisted in MongoDB; add keys here when a step collects new analytics dimensions.
 */
export const STEP_CONTEXT_FIELDS = {
  common_routing: ["routingSelection"],
  common_more_branch: ["moreBranchSelection"],
  common_filling_method: ["fillingMethod"],
  common_cegjelzo_auto: ["cegjelzoSelected"],
  ev_cegjelzo_tax_form: ["cegjelzoSelected"],
  company_cegjelzo_tax_form: ["cegjelzoSelected"],
  common_company_type: ["companyTypeFlowSelection"],
  common_kft_interstitial: ["kftInterstitialSelection"],
  common_kft_cegjelzo_interstitial: ["kftCegjelzoInterstitialSelection"],
  new_multi_company_interstitial: ["multiCompanyInterstitialSelection"],
  company_industry_select: ["industry", "companytype"],
  company_industry_activity: ["industry", "companytype"],
  ev_industry_select: ["industry", "companytype"],
  ev_industry_activity: ["industry", "companytype"],
  new_company_type: ["regCompanyTypeSelection"],
};

const searchFlowStepRegistry = createStepRegistry({
  metaByComponent: STEP_META_BY_COMPONENT,
  contextFieldsByStepId: STEP_CONTEXT_FIELDS,
});

export const { getStepMeta, getStepMetaAtIndex, getStepContextFieldKeys } =
  searchFlowStepRegistry;

export default searchFlowStepRegistry;

/** Resolve exit flow slug from the active step / store at submission time. */
export const resolveExitFlowSlug = ({ activeStepComponent, companyStore, regCompanyTypeSelection }) => {
  if (
    activeStepComponent === EvContactPage ||
    activeStepComponent === EvSuccessScreen ||
    activeStepComponent === CompanyContactPage ||
    activeStepComponent === CompanySuccessScreen
  ) {
    return "asp";
  }

  if (activeStepComponent === NewCompanyType) {
    if (regCompanyTypeSelection === "vaf") {
      return "vaf";
    }
    if (regCompanyTypeSelection === "kftreg" || companyStore?.leadtype === "KFTREG") {
      return "kftreg";
    }
    return "reg";
  }

  // Defensive/future-proofing: trackReqSent() is only ever reached from a
  // contact/success step in practice (NewMultiCompanyInterstitial's own Next
  // is a flow handoff, tracked separately via trackFlowHandoff), but this
  // keeps resolveExitFlowSlug correct if that ever changes.
  if (activeStepComponent === NewMultiCompanyInterstitial) {
    return "multicompany";
  }

  const fromStore = companyStore?.flowStart || companyStore?.leadtype || "";
  return String(fromStore).trim().toLowerCase() || "asp";
};

export { normalizeEntryFlowSlug, normalizeFlowSlug };
